import React from "react"
import clsx from "clsx"
import { Trash } from "react-bootstrap-icons"
import { Button, SecondaryButton } from "../../components/shared/Button"
import {
  cancelledTicketStatusLabel,
  isCancelledTicketStatus,
  type TicketPurchaseWithPurchaser,
} from "../../requests/ticketPurchases"

interface TicketRowDesktopProps {
  ticket: TicketPurchaseWithPurchaser
  onRedeem: (ticketId: number) => void
  onUnredeem: (ticketId: number) => void
  onDelete: (ticketId: number) => void
  onCancelRefund: (ticket: TicketPurchaseWithPurchaser) => void
  isRedeemLoading: boolean
  isUnredeemLoading: boolean
  isDeleteLoading: boolean
  isCancelRefundLoading: boolean
}

export const TicketRowDesktop: React.FC<TicketRowDesktopProps> = ({
  ticket,
  onRedeem,
  onUnredeem,
  onDelete,
  onCancelRefund,
  isRedeemLoading,
  isUnredeemLoading,
  isDeleteLoading,
  isCancelRefundLoading,
}) => {
  const cancelled = isCancelledTicketStatus(ticket.status)

  return (
  <tr
    className={clsx(
      cancelled ? "bg-gray-50 opacity-60" : ticket.redeemed ? "bg-green-50" : "hover:bg-gray-50",
    )}
  >
    <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-black-800">
      {ticket.ticket_id}
    </td>
    <td className="px-6 py-4 whitespace-nowrap">
      <div>
        <div className="text-sm font-bold text-black-800 leading-none">
          {ticket.purchaser ? ticket.purchaser.name : ticket.guest_name}
        </div>
        {ticket.purchaser ? (
          <div className="text-xs text-gray-500 mt-1">
            {ticket.purchaser.email}
          </div>
        ) : (
          <div className="text-xs text-gray-400 mt-1 italic">
            {ticket.guest_email || "Guest"}
          </div>
        )}
      </div>
    </td>
    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
      {new Date(ticket.created_at).toLocaleDateString()}
    </td>
    <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-black-800">
      ${ticket.amount}
    </td>
    <td className="px-6 py-4 whitespace-nowrap">
      {cancelled ? (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold bg-gray-200 text-gray-700">
          {cancelledTicketStatusLabel(ticket.status)}
        </span>
      ) : ticket.redeemed ? (
        <div>
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold bg-green-100 text-green-800">
            Redeemed
          </span>
          <div className="text-xs text-gray-500 mt-1">
            by {ticket.redeemed_by_name}
          </div>
          {ticket.redeemed_at && (
            <div className="text-xs text-gray-500">
              {new Date(ticket.redeemed_at).toLocaleString()}
            </div>
          )}
        </div>
      ) : (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold bg-yellow-100 text-yellow-800">
          Unredeemed
        </span>
      )}
    </td>
    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
      {!cancelled && (
        <div className="flex flex-col items-start gap-1">
          <div className="flex items-center gap-2">
            {ticket.redeemed ? (
              <SecondaryButton
                onClick={() => onUnredeem(ticket.id)}
                className="text-sm"
                disabled={isUnredeemLoading || isRedeemLoading}
              >
                Mark Unredeemed
              </SecondaryButton>
            ) : (
              <Button
                onClick={() => onRedeem(ticket.id)}
                className="text-sm"
                disabled={isRedeemLoading || isUnredeemLoading}
              >
                Mark Redeemed
              </Button>
            )}
            {ticket.status === "comped" && !ticket.redeemed && (
              <button
                type="button"
                onClick={() => onDelete(ticket.id)}
                disabled={isDeleteLoading}
                className="p-1 text-gray-600 hover:text-red-600 focus:outline-none disabled:opacity-50"
                title="Remove from guest list"
                aria-label="Remove from guest list"
              >
                <Trash className="w-4 h-4" />
              </button>
            )}
          </div>
          {ticket.status !== "comped" && ticket.can_refund && (
            <button
              type="button"
              onClick={() => onCancelRefund(ticket)}
              disabled={isCancelRefundLoading}
              className="text-xs text-red-700 hover:underline focus:outline-none disabled:opacity-50"
            >
              cancel and refund
            </button>
          )}
        </div>
      )}
    </td>
  </tr>
  )
}
